import { sub } from 'date-fns'
import { useEffect, useState } from 'react'

import { useGetPluginPingsStatusQuery } from '../../app/apiSliceCoreLogicApi'
import { Loader } from '../../common/components'
import { useDashboard } from '../../common/hooks'
import { ChartData } from '../../common/types/common'
import {
  GranularityEnum,
  OrganizationLevelEnum,
  type PluginPingsPayload,
} from '../../common/types/monitoring'
import { getColorBasedOnValuePercentage } from '../../common/utils/colorUtils'
import { getDatesInRange } from '../../common/utils/dateTimeUtils'
import ConnectivityGraph from './ConnectivityGraph'

interface Props {
  daysSelected: number
}

const TeamsDataGraph = ({ daysSelected }: Props) => {
  const { teams } = useDashboard()

  const [chartDataByTeam, setChartDataByTeam] = useState<{ [key: string]: ChartData }>({})

  const [timePeriod, setTimePeriod] = useState<{
    start_timestamp: string
    end_timestamp: string
  } | null>(null)

  const { data, isFetching } = useGetPluginPingsStatusQuery(
    {
      start_timestamp: timePeriod?.start_timestamp ?? '',
      end_timestamp: timePeriod?.end_timestamp ?? '',
      granularity: GranularityEnum.DAY,
      organization_level: OrganizationLevelEnum.TEAM,
    },
    { skip: !timePeriod },
  )

  useEffect(() => {
    const today = new Date() // Defaults time to host computer!

    setTimePeriod({
      start_timestamp: sub(today, { days: daysSelected - 1 }).toISOString(),
      end_timestamp: today.toISOString(),
    })
  }, [daysSelected])

  useEffect(() => {
    if (!timePeriod || !data) return

    // Generate the array of dates
    const datesArray = getDatesInRange(
      new Date(timePeriod.start_timestamp),
      new Date(timePeriod.end_timestamp),
    )

    const teamDataMap = data.reduce<Record<string, PluginPingsPayload>>(
      (acc, entry: PluginPingsPayload[number]) => {
        if (!entry.team_id) return acc
        acc[entry.team_id] = [...(acc[entry.team_id] ?? []), entry]
        return acc
      },
      {},
    )

    const newChartDataByTeam = Object.entries(teamDataMap).reduce<Record<string, ChartData>>(
      (acc, [teamId, teamData]) => {
        const rawChartData = datesArray.map((date) => {
          const dateStr = date.toISOString().split('T')[0] // Format date as 'YYYY-MM-DD'
          const apiDataForDate = teamData.find((d: PluginPingsPayload[number]) =>
            d.grouping_timestamp.startsWith(dateStr),
          )
          const computerCount = apiDataForDate?.computer_count ?? 0

          return {
            label: dateStr,
            count: computerCount,
            backgroundColor:
              teams[teamId]?.expectedComputerCount == null
                ? 'hsla(245,58%,51%,0.8)'
                : getColorBasedOnValuePercentage(
                    apiDataForDate ? apiDataForDate.computer_count : 0,
                    teams[teamId]?.expectedComputerCount ?? 0,
                  ),
          }
        })

        acc[teamId] = {
          labels: rawChartData.map((item) => item.label),
          datasets: [
            {
              label: 'Agents connected',
              data: rawChartData.map((item) => item.count),
              backgroundColor: rawChartData.map((item) => item.backgroundColor),
            },
          ],
        }

        return acc
      },
      {},
    )

    setChartDataByTeam(newChartDataByTeam)
  }, [data, timePeriod, teams])

  if (isFetching) return <Loader />

  return (
    <>
      {Object.values(teams).map((team) => (
        <div key={team.id} className='w-full p-2 2xl:w-1/2 5xl:w-1/3 '>
          <ConnectivityGraph
            title={team.name}
            data={chartDataByTeam[team.id]}
            expectedAgentAmount={team.expectedComputerCount ?? null}
          />
        </div>
      ))}
    </>
  )
}

export default TeamsDataGraph

